import React, { useEffect, useState } from 'react'
import { Modal, Input, Button, Upload, message } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { submitTenantDirectQuestion } from '../../utils/api'
import { FILEACTIONURL } from '../../config'
import request from '../../utils/request'
import './tenantDirectModal.scss'

const { TextArea } = Input
const MAX_IMAGES = 6

const getUploadUrl = (response) => {
    if (!response) return ''
    return response.url || response.src || response.img || ''
}

const normalizeUploadFileList = (nextList) => nextList.slice(0, MAX_IMAGES).map((file) => {
    const remoteUrl = getUploadUrl(file.response)
    if (remoteUrl) {
        return {
            ...file,
            url: remoteUrl,
            thumbUrl: remoteUrl,
        }
    }
    if (file.originFileObj && file.status === 'uploading' && !file.thumbUrl) {
        return {
            ...file,
            thumbUrl: URL.createObjectURL(file.originFileObj),
        }
    }
    return file
})

const TenantDirectQuestionModal = ({
    visible,
    onClose,
    userInfo,
}) => {
    const [description, setDescription] = useState('')
    const [contactTel, setContactTel] = useState('')
    const [fileList, setFileList] = useState([])
    const [submitting, setSubmitting] = useState(false)
    const [previewVisible, setPreviewVisible] = useState(false)
    const [previewImage, setPreviewImage] = useState('')
    const [previewTitle, setPreviewTitle] = useState('')

    useEffect(() => {
        if (!visible) return
        setContactTel(userInfo?.mobile || '')
        setDescription('')
        setFileList([])
        setSubmitting(false)
        setPreviewVisible(false)
        setPreviewImage('')
        setPreviewTitle('')
    }, [visible, userInfo])

    const handlePreview = (file) => {
        const url = file.url || file.thumbUrl || getUploadUrl(file.response)
        if (!url) return
        setPreviewImage(url)
        setPreviewTitle(file.name || '图片预览')
        setPreviewVisible(true)
    }

    const handleUploadChange = ({ fileList: nextList }) => {
        setFileList(normalizeUploadFileList(nextList))
    }

    const handleUpload = async (options) => {
        const { file, onSuccess, onError } = options
        const extension = file.name ? file.name.substring(file.name.lastIndexOf('.')) : '.jpg'
        if (!['.jpg', '.jpeg', '.png'].includes(extension.toLowerCase())) {
            message.error('请上传 jpg、jpeg 或 png 格式的图片')
            onError?.(new Error('invalid type'))
            return
        }
        if (file.size > 5 * 1024 * 1024) {
            message.error('请上传 5M 以下的图片')
            onError?.(new Error('file too large'))
            return
        }
        const fd = new FormData()
        fd.append('xslFile', file)
        fd.append('suffix', extension.replace('.', ''))
        try {
            const res = await request({
                url: FILEACTIONURL,
                method: 'post',
                headers: { 'Content-Type': 'multipart/form-data;' },
                data: fd,
            })
            if (res && Number(res.retcode) === 0 && (res.src || res.img)) {
                const url = res.src || res.img
                onSuccess?.({
                    ...res,
                    url,
                }, file)
            } else {
                message.error((res && res.message) || '上传失败')
                onError?.(new Error('upload failed'))
            }
        } catch (e) {
            message.error('上传失败')
            onError?.(e)
        }
    }

    const handleSubmit = async () => {
        const content = description.trim()
        if (!content) {
            message.error('请填写问题和意见')
            return
        }
        const tel = String(contactTel || '').trim()
        if (!tel) {
            message.error('请填写联系电话')
            return
        }
        const regTel = /^(13[0-9]|14[01456879]|15[0-35-9]|16[2567]|17[0-8]|18[0-9]|19[0-35-9])\d{8}$/
        if (!regTel.test(tel)) {
            message.error('请填写有效手机号码')
            return
        }
        const imageList = fileList
            .map((item) => item.url || getUploadUrl(item.response))
            .filter(Boolean)
        setSubmitting(true)
        try {
            const res = await submitTenantDirectQuestion({
                description: content,
                image: imageList.join(','),
                contactTel: tel,
            })
            if (res && Number(res.code) === 0) {
                message.success('提交成功')
                onClose?.()
            } else {
                message.error((res && res.message) || '提交失败')
            }
        } catch (e) {
            message.error('提交失败')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <>
        <Modal
            title="租情直通车"
            visible={visible}
            onCancel={onClose}
            destroyOnClose
            width={610}
            className="tenant-direct-question-modal-wrap"
            footer={(
                <div className="tenant-direct-question-modal__footer">
                    <Button onClick={onClose}>取消</Button>
                    <Button
                        type="primary"
                        className="tenant-direct-question-modal__submit"
                        loading={submitting}
                        onClick={handleSubmit}
                    >
                        提交
                    </Button>
                </div>
            )}
        >
            <div className="tenant-direct-question-modal">
                <div className="tenant-direct-question-modal__field tenant-direct-question-modal__field--desc">
                    <div className="tenant-direct-question-modal__label">
                        <span className="tenant-direct-question-modal__required">*</span>
                        问题和意见：
                    </div>
                    <TextArea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="请详细描述您的问题或意见"
                        maxLength={500}
                        showCount
                        rows={6}
                    />
                </div>
                <div className="tenant-direct-question-modal__field tenant-direct-question-modal__field--image">
                    <div className="tenant-direct-question-modal__label tenant-direct-question-modal__label--with-count">
                        <span>
                            图片（选填）
                            <span className="tenant-direct-question-modal__upload-tip">（最多上传6张图片）</span>
                        </span>
                        <span className="tenant-direct-question-modal__image-count">
                            {fileList.length}/{MAX_IMAGES}
                        </span>
                    </div>
                    <Upload
                        listType="picture-card"
                        fileList={fileList}
                        customRequest={handleUpload}
                        onChange={handleUploadChange}
                        onPreview={handlePreview}
                        accept=".jpg,.jpeg,.png"
                        maxCount={MAX_IMAGES}
                        beforeUpload={() => {
                            if (fileList.length >= MAX_IMAGES) {
                                message.warning(`最多上传${MAX_IMAGES}张图片`)
                                return Upload.LIST_IGNORE
                            }
                            return true
                        }}
                    >
                        {fileList.length >= MAX_IMAGES ? null : (
                            <div className="tenant-direct-question-modal__upload-btn">
                                <PlusOutlined />
                            </div>
                        )}
                    </Upload>
                </div>
                <div className="tenant-direct-question-modal__field">
                    <div className="tenant-direct-question-modal__label">
                        <span className="tenant-direct-question-modal__required">*</span>
                        联系电话
                        <span className="tenant-direct-question-modal__label-tip">
                            （默认展示您的手机号，可修改）
                        </span>
                    </div>
                    <Input
                        value={contactTel}
                        onChange={(e) => setContactTel(e.target.value)}
                    />
                </div>
            </div>
        </Modal>
        <Modal
            visible={previewVisible}
            title={previewTitle}
            footer={null}
            onCancel={() => setPreviewVisible(false)}
            zIndex={1100}
        >
            <img alt="" style={{ width: '100%' }} src={previewImage} />
        </Modal>
        </>
    )
}

export default TenantDirectQuestionModal
